import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { EMPLOYER_NAV } from "@/pages/employer/EmployerHome";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Camera, Lock, Save, Megaphone, CalendarClock, Eye } from "lucide-react";
import { CAMPUSES, ENROLLMENT_GOALS, ENROLLMENT_INTENSITY, SUPPORT_INTERESTS } from "@/lib/institution";

const navFor = (role) => ({ student: STUDENT_NAV, advisor: ADVISOR_NAV, faculty: ADVISOR_NAV, employer: EMPLOYER_NAV, admin: ADMIN_NAV }[role] || STUDENT_NAV);

const VISIBILITY_OPTS = [
  { v: "everyone", l: "Everyone at GPA+" },
  { v: "employers", l: "Employers & staff" },
  { v: "staff", l: "Advisors & staff only" },
  { v: "private", l: "Just me (hidden)" },
];

// Which professional/institutional fields each role sees. Personal + contact
// fields are shown to everyone; authorization data is never editable here.
const PRO_FIELDS = {
  student: ["campus"],
  advisor: ["job_title", "department", "campus"],
  faculty: ["job_title", "department", "campus"],
  admin: ["job_title", "department"],
  employer: ["company", "job_title"],
};
const PRO_LABEL = {
  job_title: "Job title / Position",
  department: "Department / Division",
  campus: "College / Campus",
  company: "Company / Organization",
};

function initials(name = "", email = "") {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

const FIELDS = ["first_name", "preferred_name", "last_name", "alternate_email", "phone", "preferred_contact", "about_me", "calendar_url", "profile_visibility", "campus", "enrollment_goal", "transfer_target", "transfer_major", "expected_completion", "enrollment_intensity"];

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [avatar, setAvatar] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/profile").then(({ data }) => {
    setData(data);
    setAvatar(data.avatar);
    setForm({ ...Object.fromEntries(FIELDS.map((k) => [k, data[k] || ""])), support_interests: data.support_interests || [] });
  }).catch(() => toast.error("Could not load your profile."));

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSel = (k, v) => setForm((f) => ({ ...f, [k]: v === "__none" ? "" : v }));
  const toggleSupport = (v) => setForm((f) => {
    const cur = f.support_interests || [];
    return { ...f, support_interests: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        const dataUrl = c.toDataURL("image/jpeg", 0.85);
        try {
          await api.patch("/auth/avatar", { avatar: dataUrl });
          setAvatar(dataUrl);
          if (!user.preview) setUser({ ...user, avatar: dataUrl });
          toast.success("Profile photo updated.");
        } catch { toast.error("Couldn't save your photo. Please try again."); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: updated } = await api.patch("/profile", form);
      setData(updated);
      setForm({ ...Object.fromEntries(FIELDS.map((k) => [k, updated[k] || ""])), support_interests: updated.support_interests || [] });
      if (!user.preview && updated.name) setUser({ ...user, name: updated.name });
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save your profile.");
    } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <DashboardShell title="My Profile" subtitle="Your personal and contact information" nav={navFor(user?.role)}>
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>
      </DashboardShell>
    );
  }

  const ro = data.readonly || {};
  const readonly = user?.preview;

  return (
    <DashboardShell title="My Profile" subtitle="Manage your personal and contact details. Your role and access are managed by Website Administration." nav={navFor(user?.real_role || user?.role)}>
      <form onSubmit={save} className="mx-auto max-w-3xl space-y-6" data-testid="profile-form">
        {readonly && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800" data-testid="profile-readonly-note">
            You're in preview mode. Exit preview to edit your own profile.
          </div>
        )}
        {/* Photo + identity */}
        <Card className="flex items-center gap-5 p-6 card-elevate">
          {avatar
            ? <img src={avatar} alt={data.name} className="h-20 w-20 rounded-full object-cover ring-2 ring-[#78be21]/40" data-testid="profile-avatar" />
            : <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#78be21] text-xl font-extrabold text-[#00243f]" data-testid="profile-avatar">{initials(data.name, data.email)}</span>}
          <div className="min-w-0">
            <p className="truncate text-lg font-bold text-[#003057]" data-testid="profile-name">{data.name}</p>
            <span className="mt-1 inline-block rounded-full bg-[#78be21]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#5fa114]">{data.role_label}</span>
            <div className="mt-2">
              <Button type="button" variant="outline" size="sm" disabled={readonly} onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn"
                className="rounded-full border-[#003057]/20 text-[#005291]">
                <Camera className="mr-1.5 h-4 w-4" /> {avatar ? "Change photo" : "Add photo"}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onAvatar} className="hidden" data-testid="profile-photo-input" />
            </div>
          </div>
        </Card>

        {/* Basic information */}
        <Card className="p-6 card-elevate">
          <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">Basic Information</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" data-testid="profile-first-name" /></div>
            <div><Label>Preferred name</Label><Input value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} className="mt-1.5" data-testid="profile-preferred-name" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" data-testid="profile-last-name" /></div>
          </div>
        </Card>

        {/* Contact information */}
        <Card className="p-6 card-elevate">
          <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">Contact Information</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="flex items-center gap-1.5">Primary email <Lock className="h-3 w-3 text-slate-400" /></Label>
              <Input value={data.email} disabled readOnly className="mt-1.5 bg-slate-50 text-slate-500" data-testid="profile-primary-email" />
              <p className="mt-1 text-[11px] text-slate-400">This is your login email and can't be changed here.</p>
            </div>
            <div><Label>Alternate email</Label><Input type="email" value={form.alternate_email} onChange={(e) => set("alternate_email", e.target.value)} className="mt-1.5" data-testid="profile-alternate-email" /></div>
            <div><Label>Mobile / Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="profile-phone" /></div>
            <div>
              <Label>Preferred contact method</Label>
              <Select value={form.preferred_contact || "email"} onValueChange={(v) => set("preferred_contact", v)}>
                <SelectTrigger className="mt-1.5" data-testid="profile-preferred-contact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Primary email</SelectItem>
                  <SelectItem value="alternate_email">Alternate email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Academic Plans — student self-reported; internal for enrollment/services planning */}
        {data.role === "student" && (
          <Card className="p-6 card-elevate" data-testid="profile-plans-card">
            <h3 className="mb-1 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">Academic Plans</h3>
            <p className="mb-4 text-xs text-slate-500">Optional — this is not the official registrar record. Sharing your plans helps LSC advisors improve services and plan for enrollment.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>College / Campus</Label>
                <Select value={form.campus || ""} onValueChange={(v) => setSel("campus", v)} disabled={readonly}>
                  <SelectTrigger className="mt-1.5" data-testid="profile-campus"><SelectValue placeholder="Select campus" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Not specified</SelectItem>
                    {CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Enrollment goal</Label>
                <Select value={form.enrollment_goal || ""} onValueChange={(v) => setSel("enrollment_goal", v)} disabled={readonly}>
                  <SelectTrigger className="mt-1.5" data-testid="profile-enrollment-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Not sure yet</SelectItem>
                    {ENROLLMENT_GOALS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.enrollment_goal === "transfer" && (
                <>
                  <div><Label>Target 4-year university</Label><Input value={form.transfer_target} disabled={readonly} onChange={(e) => set("transfer_target", e.target.value)} className="mt-1.5" placeholder="e.g. University of Houston" data-testid="profile-transfer-target" /></div>
                  <div><Label>Intended major</Label><Input value={form.transfer_major} disabled={readonly} onChange={(e) => set("transfer_major", e.target.value)} className="mt-1.5" data-testid="profile-transfer-major" /></div>
                </>
              )}
              <div><Label>Expected completion</Label><Input value={form.expected_completion} disabled={readonly} onChange={(e) => set("expected_completion", e.target.value)} className="mt-1.5" placeholder="e.g. Spring 2027" data-testid="profile-expected-completion" /></div>
              <div>
                <Label>Enrollment intensity</Label>
                <Select value={form.enrollment_intensity || ""} onValueChange={(v) => setSel("enrollment_intensity", v)} disabled={readonly}>
                  <SelectTrigger className="mt-1.5" data-testid="profile-enrollment-intensity"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Prefer not to say</SelectItem>
                    {ENROLLMENT_INTENSITY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label>Support I'm interested in</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SUPPORT_INTERESTS.map((o) => {
                  const on = (form.support_interests || []).includes(o.v);
                  return (
                    <button type="button" key={o.v} onClick={() => toggleSupport(o.v)} disabled={readonly} data-testid={`profile-support-${o.v}`}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-[#003057] bg-[#003057] text-white" : "border-slate-300 text-slate-600 hover:border-[#78be21]"}`}>
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Public Profile — self-marketing; visibility chosen by the user (in-app only) */}
        <Card className="p-6 card-elevate" data-testid="profile-public-card">
          <h3 className="mb-1 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">
            <Megaphone className="h-4 w-4 text-[#5fa114]" /> About Me & Availability
          </h3>
          <p className="mb-4 text-xs text-slate-500">
            {data.role === "student"
              ? "This is your chance to market yourself to employers. Share your goals, strengths and what you're looking for."
              : "Introduce yourself to others on GPA+."} Only signed-in GPA+ members you choose below can see this — it's never public or searchable on the internet.
          </p>
          <div className="space-y-4">
            <div>
              <Label>About Me</Label>
              <Textarea value={form.about_me} maxLength={2000} disabled={readonly}
                onChange={(e) => set("about_me", e.target.value)} className="mt-1.5 min-h-[120px]"
                placeholder={data.role === "student" ? "e.g. Nursing student passionate about pediatric care, strong communicator, seeking a summer clinical internship in the Houston area…" : "A short introduction others will see…"}
                data-testid="profile-about-me" />
              <p className="mt-1 text-right text-[11px] text-slate-400">{(form.about_me || "").length}/2000</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-slate-400" /> Calendar / booking link</Label>
                <Input value={form.calendar_url} disabled={readonly} onChange={(e) => set("calendar_url", e.target.value)}
                  className="mt-1.5" placeholder="https://calendly.com/you" data-testid="profile-calendar-url" />
                <p className="mt-1 text-[11px] text-slate-400">Shown to others as a “Book time” button.</p>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5 text-slate-400" /> Who can see this</Label>
                <Select value={form.profile_visibility || "employers"} onValueChange={(v) => set("profile_visibility", v)} disabled={readonly}>
                  <SelectTrigger className="mt-1.5" data-testid="profile-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBILITY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-slate-400">Applies to About Me, contact info & calendar link.</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Institutional information — READ-ONLY; managed by Website Administration */}
        {(ro.campus || ro.program || ro.job_title || ro.department || ro.company || ro.student_number || ro.employee_id) && (
          <Card className="p-6 card-elevate">
            <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">
              {data.role === "employer" ? "Professional Information" : "Institutional Information"}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                ...(data.role === "student" ? [] : [["College / Campus", ro.campus, "campus"]]),
                [data.role === "student" ? "Academic Program" : "Program / Area", ro.program, "program"],
                ["Job Title", ro.job_title, "job-title"],
                ["Department / Division", ro.department, "department"],
                ["Company / Organization", ro.company, "company"],
                ["Student ID", ro.student_number, "student-id"],
                ["Employee ID", ro.employee_id, "employee-id"],
              ].filter(([, v]) => v).map(([label, v, tid]) => (
                <div key={tid}>
                  <Label className="flex items-center gap-1.5">{label} <Lock className="h-3 w-3 text-slate-400" /></Label>
                  <Input value={v} disabled readOnly className="mt-1.5 bg-slate-50 text-slate-500" data-testid={`profile-${tid}-ro`} />
                </div>
              ))}
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Institutional information is managed by Website Administration. Contact an administrator to update it.</p>
          </Card>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={busy || readonly} className="rounded-full bg-[#003057] hover:bg-[#00223f]" data-testid="profile-save-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
          </Button>
        </div>
      </form>
    </DashboardShell>
  );
}
